import { Button, Form, Modal } from 'react-bootstrap'
import { ListingAdjustment } from '../../utilities/listings.interfaces'
import { DateTime } from 'luxon'
import { ArrowRight, Truck } from 'react-bootstrap-icons'
import DeliveryTruckSvg from '../../../common/components/svg/DeliveryTruckSvg'
import { SALESFORCE_URLS_BY_ENVIRONMENT } from '../../../common/common.entities'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface ListingsAdjustmentsModalProps {
  adjustments: ListingAdjustment[]
  productMpn: string
  modalVisible: boolean
  closeModal: any
  title: string
  environment: string
}

export default function ListingsAdjustmentsModal(props: ListingsAdjustmentsModalProps){
  if(!props.adjustments) return <></>

  const [uniqueFrom, setUniqueFrom] = useState<string[]>([])
  const [uniqueTo, setUniqueTo] = useState<string[]>([])
  const [filtered, setFiltered] = useState<ListingAdjustment[]>([])
  const [filterText, setFilterText] = useState<string>(null)

  useEffect(() => {
    const from = []
    const to = []
    for(const adj of props.adjustments){
      const sourceWarehouse = adj.Source_Inventory__r?.Warehouse__r?.Name?.trim()
      const targetWarehouse = adj.Target_Inventory__r?.Warehouse__r?.Name?.trim()
      if(sourceWarehouse && !from.includes(sourceWarehouse)){
        from.push(sourceWarehouse)
      }
      if(targetWarehouse && !to.includes(targetWarehouse)){
        to.push(targetWarehouse)
      }
      setUniqueFrom(from)
      setUniqueTo(to)
    }
  }, [props.adjustments])

  const handleFromWarehouseChanged = useCallback((e) => {
    if(e.target.value === 'all'){
      setFiltered([])
      setFilterText(null)
      return
    }
    const filtered = props.adjustments.filter(adj => adj.Source_Inventory__r?.Warehouse__r?.Name === e.target.value)
    setFiltered(filtered)
    setFilterText(`${filtered.length} ${e.target.value} adjustment(s) for ${props.productMpn}.`)
  }, [props])

  const handleToWarehouseChanged = useCallback((e) => {
    if(e.target.value === 'all'){
      setFiltered([])
      setFilterText(null)
      return
    }
    const filtered = props.adjustments.filter(adj => adj.Target_Inventory__r?.Warehouse__r?.Name === e.target.value)
    setFiltered(filtered)
    setFilterText(`${filtered.length} ${e.target.value} adjustment(s) for ${props.productMpn}.`)
  }, [props])

  const view = useMemo(() => {
    const list = filtered.length > 0 ? filtered : props.adjustments
    const totalQty = list.reduce((total, t) => total + t.Quantity__c, 0).toLocaleString()
    return <Modal show={props.modalVisible} animation={false} onHide={props.closeModal} className='modal-xl'>
      <Modal.Header 
        closeButton
        style={{ 
          position: 'sticky', 
          top: -1, 
          zIndex: 1050, 
          backgroundColor: 'white', 
          borderBottom: '1px solid #ddd'
        }}
      >
        <Modal.Title>
          <span className='beacon-header'>{props.title}</span>
        </Modal.Title>
      </Modal.Header>
        
      <Modal.Body>
        <div style={{display: 'flex', flexWrap: 'wrap', gap: '5px'}}>
          <div>
            <span className='beacon-label'>FROM WAREHOUSE</span>
            <Form.Select onChange={handleFromWarehouseChanged}>
              <option value='all'>ALL</option>
              {uniqueFrom.map(uf => (
                <option value={uf}>{uf}</option>
              ))}
            </Form.Select>
          </div>
          <div>
            <span className='beacon-label'>TO WAREHOUSE</span>
            <Form.Select onChange={handleToWarehouseChanged}>
              <option value='all'>ALL</option>
              {uniqueTo.map(ut => (
                <option value={ut}>{ut}</option>
              ))}
            </Form.Select>
          </div>
        </div>
        {filterText && <span className='beacon-label'>{filterText} Total adjustments quantity: {totalQty}</span>}
        {!filterText && <span className='beacon-label'>{props.adjustments.length} adjustment(s) for {props.productMpn}. Total adjustments quantity: {totalQty}</span>}
        <div style={{fontSize: 'larger'}}>
          <table className='flexport-table'>
            <thead>
              <tr className='sticky-row'>
                <th>
                  <span className='beacon-header'>GROUP</span>
                </th>
                <th>
                  <span className='beacon-header'>ADJUSTMENT</span>
                </th>
                <th>
                  <span className='beacon-header'>FROM/TO</span>
                </th>
                <th>
                  <span className='beacon-header'>STATUS</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list?.map(ia => (
                <tr>
                  <td>
                    <div>
                      {ia.Inventory_Adjustment_Group__r && <a title='Opens the Adjustment Group in Mission Control'
                        href={`${SALESFORCE_URLS_BY_ENVIRONMENT[props.environment]}/${ia.Inventory_Adjustment_Group__r?.Id}`}  className='beacon-link' target="_blank" rel="noopener noreferrer">
                        {ia.Inventory_Adjustment_Group__r?.Name ?? 'NO IAG'} {ia.Inventory_Adjustment_Group__r?.Shipment_Name__c} {ia.Inventory_Adjustment_Group__r?.BOL_Number__c}
                      </a>
                      }                    
                      <br/>
                      <span className='beacon-label' style={{fontSize: 'smaller'}}>
                        {DateTime.fromISO(ia.CreatedDate).toFormat('MM/dd/yy t')}<br/>
                        {ia.Inventory_Adjustment_Group__r?.Date_Received__c ? `RECEIVED: ${ia.Inventory_Adjustment_Group__r?.Date_Received__c}` : 'NOT RECEIVED'}
                        <br/>
                        {ia.Inventory_Adjustment_Group__r?.Adjustment_Reason__c}
                      </span>
                    </div>
                  </td>
                  <td>
                    <a title='Opens the Adjustment in Mission Control' href={`${SALESFORCE_URLS_BY_ENVIRONMENT[props.environment]}/${ia.Id}`}  className='beacon-link' target="_blank" rel="noopener noreferrer">{ia.Name}</a>
                    <span className='beacon-label' style={{fontSize: 'smaller'}}>
                      <br/>
                      QTY: {ia.Quantity__c}
                      <br/>
                      {ia.Adjustment_Reason__c}
                    </span>
                  </td>
                  <td>
                    {ia.Source_Inventory__r?.Warehouse__r?.Name ?? ia.Inventory_Adjustment_Group__r?.Source_Location__r?.Name}<br/><DeliveryTruckSvg height={25}/><ArrowRight/><br/>{ia.Target_Inventory__r?.Warehouse__r?.Name}
                  </td>
                  <td>
                    {ia.Inventory_Adjustment_Group__r?.Status__c}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={props.closeModal}>CLOSE</Button>
      </Modal.Footer>
    </Modal>
  }, [filtered, props, uniqueFrom, uniqueTo])

  return view
}